import { useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { TimeField } from '@/components/pages/work-plan/planner-bits';
import { fetchWorkSessions, putWorkSessions } from '@/lib/api/work-plan';
import {
  DEFAULT_SESSION_DEFS,
  SESSION_LABEL_MAX,
  normalizeSessionDefs,
  sessionDefsEqual,
  validateSessionDefs,
} from '@/lib/work-plan-sessions';

// Editor for the two Work Plan sessions (global settings, org-wide): label + hours for the
// fixed `am` and `pm` keys. The keys never change — they are wire values on postings, MC
// requests and copy scopes — so the rows are fixed too; only the words and the clock times
// move. Validation mirrors the server (label 1–12, HH:MM, start < end, AM ends before PM
// starts) so a bad save is refused here with the reason inline. An older backend has no
// /api/work-plan/sessions (404) → read-only "not supported" note.

function SessionRow({ row, errors, canEdit, onChange }) {
  const rangeError = errors[`${row.key}.range`];
  const labelError = errors[`${row.key}.label`];
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-card px-3 py-2.5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex min-w-0 flex-1 flex-col gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {row.key.toUpperCase()} label
          <Input
            value={row.label}
            onChange={(event) => onChange({ label: event.target.value })}
            disabled={!canEdit}
            maxLength={SESSION_LABEL_MAX}
            placeholder={row.key.toUpperCase()}
            className={cn('h-8 text-sm normal-case tracking-normal', labelError && 'border-destructive')}
            aria-label={`${row.key.toUpperCase()} session label`}
          />
        </label>
        <label className="flex w-32 flex-col gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Starts
          <TimeField
            value={row.start}
            onChange={(event) => onChange({ start: event.target.value })}
            disabled={!canEdit}
            aria-label={`${row.key.toUpperCase()} session start`}
            className={cn(rangeError && 'border-destructive')}
          />
        </label>
        <label className="flex w-32 flex-col gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Ends
          <TimeField
            value={row.end}
            onChange={(event) => onChange({ end: event.target.value })}
            disabled={!canEdit}
            aria-label={`${row.key.toUpperCase()} session end`}
            className={cn(rangeError && 'border-destructive')}
          />
        </label>
      </div>
      {labelError || rangeError ? (
        <p className="text-[11px] text-destructive">{[labelError, rangeError].filter(Boolean).join(' ')}</p>
      ) : null}
    </div>
  );
}

export function SessionsEditor({ canEdit = false }) {
  const [rows, setRows] = useState(() => normalizeSessionDefs(DEFAULT_SESSION_DEFS));
  const [baseline, setBaseline] = useState(() => normalizeSessionDefs(DEFAULT_SESSION_DEFS));
  const [serverCanEdit, setServerCanEdit] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [unsupported, setUnsupported] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchWorkSessions()
      .then((res) => {
        if (cancelled) return;
        const defs = normalizeSessionDefs(res.sessions);
        setRows(defs);
        setBaseline(defs);
        setServerCanEdit(Boolean(res.canEdit));
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setIsLoading(false);
        if (error?.status === 404) {
          setUnsupported(true);
          return;
        }
        setLoadFailed(true);
        toast.error(error instanceof Error ? error.message : 'Could not load the session hours.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const validation = validateSessionDefs(rows);
  const isDirty = !sessionDefsEqual(rows, baseline);
  // The page prop is the RBAC gate; the payload's canEdit is the server's own answer. Both
  // must agree, and nothing is editable on a server that has no endpoint or failed to load.
  const editable = canEdit && serverCanEdit && !unsupported && !loadFailed;

  const updateRow = (key, patch) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const handleSave = async () => {
    if (!editable || !validation.ok || !isDirty) return;
    setIsSaving(true);
    try {
      const res = await putWorkSessions({
        sessions: rows.map((row) => ({ key: row.key, label: row.label.trim(), start: row.start, end: row.end })),
      });
      const defs = normalizeSessionDefs(res.sessions);
      setRows(defs);
      setBaseline(defs);
      toast.success('Session hours saved for everyone.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save the session hours.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        Loading session hours…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {unsupported ? (
        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Configurable session hours are not supported by this server yet — the Work Plan keeps its built-in
          AM/PM hours until the backend is updated.
        </p>
      ) : null}
      {loadFailed ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          The session hours could not be loaded. Saving now would overwrite the stored values — reload before editing.
        </p>
      ) : null}

      <div className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <SessionRow
            key={row.key}
            row={row}
            errors={validation.errors}
            canEdit={editable}
            onChange={(patch) => updateRow(row.key, patch)}
          />
        ))}
        {validation.errors.gap ? <p className="px-1 text-[11px] text-destructive">{validation.errors.gap}</p> : null}
      </div>

      {editable ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex-1" />
          {isDirty ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="[&_svg]:size-3.5"
              onClick={() => setRows(baseline)}
              disabled={isSaving}
            >
              <RotateCcw />
              Discard changes
            </Button>
          ) : null}
          <Button type="button" size="sm" onClick={handleSave} disabled={!isDirty || !validation.ok || isSaving}>
            {isSaving ? 'Saving…' : 'Save session hours'}
          </Button>
        </div>
      ) : unsupported ? null : (
        <p className="text-xs text-muted-foreground">
          You need the scheduling <span className="font-semibold">manage</span> permission to edit these.
        </p>
      )}

      <p className="text-[11.5px] leading-relaxed text-muted-foreground">
        These are the default hours a posting inherits (planners can still override a single posting) and the
        AM/PM split the planner uses to bucket doctor sessions and appointment counts: anything starting before
        the AM end counts as AM. Labels show everywhere the sessions are named — planner headers, the roster
        rail, MC requests and the phone.
      </p>
    </div>
  );
}
